import { type ReactNode } from 'react';
import { Platform, StyleSheet } from 'react-native';

import { Text, XStack, YStack, type YStackProps, tokens } from '@fhs/ui';

import type { HeadingTextProps } from '../text';

/** Web: section titles must be real headings + tab stops so VoiceOver/NVDA reach them above radiogroups. */
function webListItemGroupSectionHeadingProps(): Partial<HeadingTextProps> {
  if (Platform.OS !== 'web') {
    return {};
  }
  return {
    tabIndex: 0,
    role: 'heading',
    'aria-level': 2,
  } as Partial<HeadingTextProps>;
}

export type ListItemGroupSectionProps = YStackProps & {
  heading: ReactNode;
  description?: ReactNode;
  errorMessage?: string;
};

export function ListItemGroupSection({
  children,
  heading,
  description,
  errorMessage,
  ...yStackProps
}: ListItemGroupSectionProps) {
  const hasError = Boolean(errorMessage);
  return (
    <YStack {...yStackProps} style={[styles.section, yStackProps.style]}>
      <XStack style={styles.sectionHeadingArea}>
        <YStack>
          {typeof heading === 'string' ? (
            <Text.Heading
              type="four"
              style={hasError && styles.errorText}
              {...webListItemGroupSectionHeadingProps()}
            >
              {heading}
            </Text.Heading>
          ) : (
            heading
          )}
          {typeof description === 'string' ? (
            <Text.Paragraph
              size="md"
              {...(Platform.OS === 'web' && description.trim().length > 0
                ? ({ tabIndex: 0, accessibilityRole: 'text' } as const)
                : {})}
            >
              {description}
            </Text.Paragraph>
          ) : (
            description
          )}
        </YStack>
        {hasError && (
          <Text.Heading type="four" style={styles.errorText} aria-role="alert">
            {errorMessage}
          </Text.Heading>
        )}
      </XStack>

      {children}
    </YStack>
  );
}

const styles = StyleSheet.create({
  section: {
    gap: 12,
  },
  errorText: {
    color: tokens.colors.$error,
  },
  sectionHeadingArea: {
    flexGrow: 1,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});
